import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Button,
  Spinner,
  Modal,
  ModalBody,
  Badge,
  Popover,
  PopoverBody,
  Nav,
  NavItem,
  NavLink
} from "reactstrap";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faTrophy,
  faClock,
  faBell,
  faBellSlash,
  faSearch,
  faCalendarAlt,
  faCalendarWeek,
  faCheckCircle,
  faTimes,
  faMapMarkerAlt,
  faLayerGroup,
  faTrash,
  faCheckDouble,
  faAngleDown,
  faCircle
} from "@fortawesome/free-solid-svg-icons";
import classnames from 'classnames';
import cricketService from "../../services/api/cricket-service";

// --- HELPER: Smart Date Selector ---
const DateSelector = ({ currentDate, onDateChange }) => {
    const [mode, setMode] = useState('months'); // 'months' | 'years'
    const [tempYear, setTempYear] = useState(currentDate.getFullYear());
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const generateYears = () => {
        const current = new Date().getFullYear();
        const years = [];
        for (let i = current - 2; i <= current + 3; i++) years.push(i);
        return years;
    };

    const handleMonthClick = (monthIndex) => {
        const newDate = new Date(currentDate);
        newDate.setFullYear(tempYear);
        newDate.setMonth(monthIndex);
        onDateChange(newDate);
    };

    return (
        <div className="custom-date-picker">
            <div className="picker-header">
                <span className={`picker-btn ${mode === 'months' ? 'active' : ''}`} onClick={() => setMode('months')}>
                    {months[currentDate.getMonth()]}
                </span>
                <span className="mx-1">,</span>
                <span className={`picker-btn ${mode === 'years' ? 'active' : ''}`} onClick={() => setMode('years')}>
                    {tempYear}
                </span>
            </div>
            <div className="picker-grid">
                {mode === 'months' && months.map((m, i) => (
                    <div 
                        key={m} 
                        className={`picker-cell ${currentDate.getMonth() === i && tempYear === currentDate.getFullYear() ? 'selected' : ''}`}
                        onClick={() => handleMonthClick(i)}
                    >
                        {m}
                    </div>
                ))}
                {mode === 'years' && generateYears().map(y => (
                    <div 
                        key={y} 
                        className={`picker-cell ${tempYear === y ? 'selected' : ''}`}
                        onClick={() => { setTempYear(y); setMode('months'); }}
                    >
                        {y}
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- HELPER: Date Checkers ---
const isMatchPast = (match) => {
    return match.status === 'completed';
};
const isMatchLive = (match) => {
    if (match.status === 'started') return true;
    if (!match || !match.start_date_obj) return false;
    const start = new Date(match.start_date_obj).getTime();
    const now = Date.now();
    const endBuffer = 8 * 60 * 60 * 1000; 
    return now >= start && now <= (start + endBuffer);
};

// --- SUB-COMPONENT: Tournament Chip ---
const CricketTournamentGroup = ({ 
    group, 
    date, 
    toggling, 
    handleToggle, 
    setSelectedMatch,
    handleGroupBatchToggle,
    isMatchModalOpen,
    allMatches = [],
    isMobileView 
}) => {
  
  const [isOpen, setIsOpen] = useState(false);
  const [targetEl, setTargetEl] = useState(null); 
  const openTimeout = useRef(null);
  const closeTimeout = useRef(null);
  const [batchLoading, setBatchLoading] = useState(false);

  useEffect(() => {
    if (isMatchModalOpen) setIsOpen(false);
  }, [isMatchModalOpen]);

  const handleMouseEnter = () => {
    if (isMatchModalOpen || isMobileView) return; // Disable hover on mobile
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    openTimeout.current = setTimeout(() => setIsOpen(true), 300);
  };

  const handleMouseLeave = () => {
    if (isMatchModalOpen || isMobileView) return;
    if (openTimeout.current) clearTimeout(openTimeout.current);
    closeTimeout.current = setTimeout(() => setIsOpen(false), 200);
  };

  // Toggle on click for mobile or desktop
  const handleClick = (e) => {
    e.stopPropagation();
    if (isMatchModalOpen) return;
    setIsOpen(!isOpen);
  };

  const formatTime = (dateObj) => {
    return new Date(dateObj).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  };

  // Status Logic
  let chipClass = 'chip-upcoming'; 
  let dotColor = '#1967d2';

  const hasLive = group.matches.some(m => isMatchLive(m));
  const hasActive = group.matches.some(m => m.is_enabled);
  const isAllCompleted = group.matches.every(m => isMatchPast(m));
  const wasActiveAndFinished = group.matches.some(m => m.is_enabled && isMatchPast(m));

  // --- REVISED BATCH LOGIC ---
 const tournamentMatches = useMemo(() => {
    return allMatches.filter(m => m.tournament_slug === group.info.slug);
  }, [allMatches, group.info.slug]);

  const upcomingTournamentMatches = useMemo(() => {
    return tournamentMatches.filter(m => !isMatchPast(m));
  }, [tournamentMatches]);

  const hasUpcoming = upcomingTournamentMatches.length > 0;

  const upcomingActiveCount = upcomingTournamentMatches.filter(m => m.is_enabled).length;
  
  const showSubAllButton = hasUpcoming && upcomingActiveCount < upcomingTournamentMatches.length;
  const showUnsubAllButton = hasUpcoming && upcomingActiveCount > 0;

  const onBatchAction = async (e, targetStatus) => {
    e.stopPropagation();
    setBatchLoading(true);
    await handleGroupBatchToggle(group, targetStatus);
    setBatchLoading(false);
  };

  if (hasLive) { chipClass = 'chip-live'; dotColor = '#ea4335'; }
  else if (isAllCompleted && wasActiveAndFinished) { chipClass = 'chip-active-completed'; dotColor = '#1e8e3e'; }
  else if (hasActive) { chipClass = 'chip-active'; dotColor = '#34a853'; }
  else if (isAllCompleted) { chipClass = 'chip-completed'; dotColor = '#5f6368'; }

  return (
    <div className="event-group-wrapper" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div 
        ref={setTargetEl} 
        className={`event-chip ${chipClass} ${isMobileView ? 'mobile-dot-view' : ''}`}
        onClick={handleClick}
      >
        {isMobileView ? (
             /* Mobile View: Just a dot */
            <FontAwesomeIcon icon={faCircle} style={{color: dotColor, fontSize: '6px'}} />
        ) : (
            /* Desktop View: Full Chip */
            <>
                <span className="title-text">{group.info.name}</span>
                <span className="count-badge">{group.matches.length} M</span>
                {hasLive && <span className="live-dot"></span>}
            </>
        )}
      </div>

      {targetEl && !isMatchModalOpen && (
        <Popover placement="auto" isOpen={isOpen} target={targetEl} toggle={() => setIsOpen(!isOpen)} trigger="legacy" container="body" className="custom-popover-shadow super-priority-popover">
          <PopoverBody className="match-popover-body" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <div className={`popover-header-modern ${chipClass}-header`}>
              <div className="d-flex justify-content-between align-items-start">
                  <div style={{ flex: 1 }}>
                    <div className="popover-tournament-name">{group.info.name}</div>
                    <div className="popover-date-badge">
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                        {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </div>
                  </div>

              {/* --- BUTTON GROUP --- */}
              {!isAllCompleted && (
                <div className="d-flex flex-column gap-1 ms-2">
                  {batchLoading ? (
                    <Spinner size="sm" color="primary" />
                  ) : (
                    <>
                      {showSubAllButton && (
                        <Button size="sm" color="primary" className="py-0 px-2" style={{ fontSize: '9px', height: '20px', whiteSpace: 'nowrap' }} onClick={(e) => onBatchAction(e, true)}>
                          <FontAwesomeIcon icon={faCheckDouble} className="me-1" /> Sub All
                        </Button>
                      )}
                      
                      {showUnsubAllButton && (
                        <Button size="sm" color="danger" className="py-0 px-2" style={{ fontSize: '9px', height: '20px', whiteSpace: 'nowrap' }} onClick={(e) => onBatchAction(e, false)}>
                          <FontAwesomeIcon icon={faTimes} className="me-1" /> Unsub All
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
              </div>
            </div>
            <div className="popover-matches-list">
              {group.matches.map(m => {
                const isCompleted = isMatchPast(m);
                const isLiveNow = isMatchLive(m);
                const isLiveBlogActive = m.is_enabled;
                let rowClass = isLiveNow ? "row-is-live" : (isCompleted && isLiveBlogActive ? "row-was-active" : (isLiveBlogActive ? "row-is-active" : (isCompleted ? "row-is-completed" : "")));

                return (
                  <div key={m.match_id} className={`popover-match-row ${rowClass}`}>
                    <div className="match-time-col">
                        {isCompleted ? <span className="status-finished">Ended</span> : isLiveNow ? <span className="status-live">LIVE</span> : <span className="status-time">{formatTime(m.start_date_obj)}</span>}
                    </div>
                    <div className="match-details-col" onClick={() => { setSelectedMatch(m); setIsOpen(false); }}>
                      <div className="match-teams" title={m.title}>{m.title}</div>
                      <div className="match-subtext">{m.venue || 'Venue TBD'}</div>
                    </div>
                    <div className="match-action-col">
                      {isCompleted ? (
                         <FontAwesomeIcon icon={faCheckCircle} className={isLiveBlogActive ? "text-success" : "text-muted"} title="Completed" />
                      ) : (
                        <Button size="sm" color="link" className="action-btn" disabled={toggling[m.match_id]} onClick={(e) => handleToggle(m, e)} title={isLiveBlogActive ? "Unsubscribe" : "Subscribe"}>
                            {toggling[m.match_id] ? <Spinner size="sm" color="primary"/> : <FontAwesomeIcon icon={isLiveBlogActive ? faBell : faBellSlash} className={isLiveBlogActive ? "icon-active" : "icon-inactive"} size="lg" />}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </PopoverBody>
        </Popover>
      )}
    </div>
  );
};

// --- MAIN COMPONENT ---
const CricketLiveBlogManager = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("month"); 
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeepFetching, setIsDeepFetching] = useState(false);
  const [toggling, setToggling] = useState({});
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);
  const [morePopoverState, setMorePopoverState] = useState({ isOpen: false, targetId: null, items: [], dateLabel: null, fullDate: null });
  
  // Header & Filter States
  const [showSearch, setShowSearch] = useState(false);
  const [filters, setFilters] = useState({ query: "", tslug: "" });
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [tournaments, setTournaments] = useState([]);
  const [showUpcomingOnly, setShowUpcomingOnly] = useState(false);

  // Popover States (Rich Header)
  const [subPopoverOpen, setSubPopoverOpen] = useState(false);
  const subPopoverTimer = useRef(null);
  const [upcomingPopoverOpen, setUpcomingPopoverOpen] = useState(false);
  const [upcomingPopoverTab, setUpcomingPopoverTab] = useState('matches');
  const popoverCloseTimer = useRef(null);

  // Batch Selection States
  const [selectedUpcomingIds, setSelectedUpcomingIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // New UI States (Fixes)
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const scrollCooldownRef = useRef(false); // Ref for scroll cooldown

  const calendarRef = useRef(null);

  // Listener for mobile view resizing
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- DERIVED DATA ---
  const allUpcomingMatches = useMemo(() => {
    const now = Date.now();
    return matches.filter(m => {
        const start = new Date(m.start_date_obj).getTime();
        return start > now || (m.status !== 'completed');
    }).sort((a,b) => new Date(a.start_date_obj) - new Date(b.start_date_obj));
  }, [matches]);

  const upcomingTournamentsList = useMemo(() => {
    const map = {};
    allUpcomingMatches.forEach(m => {
        const tSlug = m.tournament_slug || "unknown";
        if(!map[tSlug]) {
            map[tSlug] = {
                info: { name: m.tournament_name, slug: tSlug },
                matches: [],
                tSlug: tSlug
            };
        }
        map[tSlug].matches.push(m);
    });
    return Object.values(map).sort((a,b) => a.info.name.localeCompare(b.info.name));
  }, [allUpcomingMatches]);

  // --- STAT CALCULATORS ---
  const getActiveSubscribedCount = () => matches.filter(m => m.is_enabled && !isMatchPast(m)).length;
  const getCompletedSubscribedCount = () => matches.filter(m => m.is_enabled && isMatchPast(m)).length;
  const getSubscribedMatchList = () => {
    const active = matches.filter(m => m.is_enabled && !isMatchPast(m)).sort((a,b) => new Date(a.start_date_obj) - new Date(b.start_date_obj));
    const finished = matches.filter(m => m.is_enabled && isMatchPast(m)).sort((a,b) => new Date(b.start_date_obj) - new Date(a.start_date_obj));
    return [...active, ...finished];
  };

  // --- HANDLERS ---
  const handleWheelScroll = (e) => {
    if (e.target.closest('.popover') || e.target.closest('.modal') || e.target.closest('.popover-matches-list')) return; 
    
    // Prevent scrolling if on cooldown
    if (scrollCooldownRef.current) return;

    if(morePopoverState.isOpen) setMorePopoverState(prev => ({ ...prev, isOpen: false }));
    
    if (e.deltaY > 0) navigateDate(1);
    else navigateDate(-1);

    // Activate Cooldown
    scrollCooldownRef.current = true;
    setTimeout(() => {
        scrollCooldownRef.current = false;
    }, 800); 
  };

  const handleOpenMatchModal = (match) => {
    setSelectedMatch(match);
    setIsMatchModalOpen(true);
    setSubPopoverOpen(false);
    setUpcomingPopoverOpen(false);
    setMorePopoverState(prev => ({ ...prev, isOpen: false }));
  };

  const handleCloseMatchModal = () => {
    setSelectedMatch(null);
    setIsMatchModalOpen(false);
  };

  // Popover Hover Handlers
  const handleSubscribedEnter = () => {
    if (isMatchModalOpen) return;
    if (subPopoverTimer.current) clearTimeout(subPopoverTimer.current);
    setSubPopoverOpen(true);
  };

  const handleSubscribedLeave = () => {
    subPopoverTimer.current = setTimeout(() => setSubPopoverOpen(false), 300);
  };

  const handleUpcomingEnter = () => {
    if (isMatchModalOpen) return;
    if (popoverCloseTimer.current) clearTimeout(popoverCloseTimer.current);
    setUpcomingPopoverOpen(true);
  };

  const handleUpcomingLeave = () => {
    popoverCloseTimer.current = setTimeout(() => setUpcomingPopoverOpen(false), 300);
  };

  // Batch Selection Handlers
  const toggleSelectAll = () => {
    if (selectedUpcomingIds.size === allUpcomingMatches.length) setSelectedUpcomingIds(new Set());
    else setSelectedUpcomingIds(new Set(allUpcomingMatches.map(m => m.match_id)));
  };

  const toggleMatchSelection = (id) => {
    const newSet = new Set(selectedUpcomingIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedUpcomingIds(newSet);
  };

  // --- API CALLS ---
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await cricketService.getTournaments();
        let list = Array.isArray(response) ? response : (response?.data || []);
        setTournaments(list);
      } catch (err) { console.error(err); }
    };
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(filters.query), 500);
    return () => clearTimeout(timer);
  }, [filters.query]);

  // --- THE AGGREGATOR FETCHER ---
  const fetchDeepCalendar = async () => {
      setLoading(true);
      setIsDeepFetching(true);
      try {
          const tourneyResponse = await cricketService.getTournaments();
          const tourneyList = Array.isArray(tourneyResponse) ? tourneyResponse : (tourneyResponse?.data || []);
          
          if(tourneyList.length === 0) {
              toast.warn("No active tournaments found.");
              return;
          }

          const promises = tourneyList.map(t => cricketService.getMatchesByTournament(t.slug));
          const results = await Promise.allSettled(promises);

          let deepMatches = [];
          results.forEach(res => {
              if(res.status === 'fulfilled' && res.value?.data) {
                 const matches = Array.isArray(res.value.data) ? res.value.data : [];
                 deepMatches = [...deepMatches, ...matches];
              }
          });

          const seenIds = new Set();
          const uniqueDeepMatches = deepMatches.filter(m => {
              const duplicate = seenIds.has(m.match_id);
              seenIds.add(m.match_id);
              return !duplicate;
          });

          uniqueDeepMatches.sort((a, b) => new Date(a.start_date_obj) - new Date(b.start_date_obj));
          setMatches(uniqueDeepMatches);
          
      } catch(err) {
          console.error(err);
          toast.error("Deep fetch failed.");
      } finally {
          setLoading(false);
          setIsDeepFetching(false);
      }
  };

  const fetchMatches = useCallback(async (isFilterChange = false) => {
    if(isDeepFetching && !isFilterChange) return;

    setLoading(true);
    try {
      let data = [];
      if (filters.tslug) {
          const response = await cricketService.getMatchesByTournament(filters.tslug);
          data = response.status === 'success' ? response.data : (response || []);
      } else {
          const response = await cricketService.getRecentMatches();
          data = response.status === 'success' ? response.data : (response || []);
      }
      
      data.sort((a, b) => new Date(a.start_date_obj) - new Date(b.start_date_obj));
      setMatches(data || []);

      if (isFilterChange && data.length > 0) {
        const now = new Date();
        const upcoming = data.find(m => new Date(m.start_date_obj) > now);
        if (upcoming) setCurrentDate(new Date(upcoming.start_date_obj));
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch matches.");
    } finally {
      setLoading(false);
    }
  }, [filters.tslug]); 

    useEffect(() => {
  if (!filters.tslug) {
    fetchDeepCalendar(); 
  } else {
    fetchMatches(true);
  }
}, [filters.tslug]);

  // --- ACTIONS ---
  const handleToggle = async (matchItem, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const matchId = matchItem.match_id;
    const newStatus = !matchItem.is_enabled;

    setToggling(prev => ({ ...prev, [matchId]: true }));
    setMatches(prev => prev.map(m => m.match_id === matchId ? { ...m, is_enabled: newStatus } : m));
    if (selectedMatch && selectedMatch.match_id === matchId) setSelectedMatch(prev => ({ ...prev, is_enabled: newStatus }));

    try {
      await cricketService.toggleLiveBlog({ 
          match_id: matchId, 
          status: newStatus, 
          title: matchItem.title, 
          match_date: matchItem.start_date_obj,
          tournament_slug: matchItem.tournament_slug 
      });
      toast.success(newStatus ? "Match subscribed successfully" : "Match unsubscribed successfully");
    } catch (err) {
      setMatches(prev => prev.map(m => m.match_id === matchId ? { ...m, is_enabled: !newStatus } : m));
      toast.error("Failed to update status.");
    } finally {
      setToggling(prev => ({ ...prev, [matchId]: false }));
    }
  };

 const handleGroupBatchToggle = async (group, targetStatus) => {
      let tSlug = group.info?.slug;
      if (!tSlug && group.tSlug) tSlug = group.tSlug; 
      let label = group.info?.name || "Tournament";
      let matchesToTarget = [];

      if (tSlug) {
          matchesToTarget = matches.filter(m => 
              m.tournament_slug === tSlug && 
              !isMatchPast(m) 
          );
      } else if (filters.tslug) {
           matchesToTarget = allUpcomingMatches.filter(m => !isMatchPast(m));
      }

      if (matchesToTarget.length === 0) {
          toast.warn("No upcoming matches found for this tournament.");
          return;
      }

      const payload = {
        matches: matchesToTarget.map(m => ({
            match_id: m.match_id,
            title: m.title,
            date: m.start_date_obj,
            tslug: m.tournament_slug
        })),
        status: targetStatus
      };

      try {
        await cricketService.toggleLiveBlogBatch(payload);
        
        setMatches(prev => prev.map(m => {
            if(matchesToTarget.find(t => t.match_id === m.match_id)) {
                return { ...m, is_enabled: targetStatus };
            }
            return m;
        }));
        
        toast.success(`${label}: ${targetStatus ? "All future matches subscribed" : "All future matches unsubscribed"}`);
      } catch (err) {
        console.error(err);
        toast.error("Group action failed.");
      }
  };

  const handleBulkAction = async () => {
    if (selectedUpcomingIds.size === 0) return;
    setBulkLoading(true);

    const matchesToToggle = allUpcomingMatches
        .filter(m => selectedUpcomingIds.has(m.match_id))
        .filter(m => !isMatchPast(m));
    const areAllActive = matchesToToggle.every(m => m.is_enabled);
    const targetStatus = !areAllActive;

    const payload = {
        matches: matchesToToggle.map(m => ({
            match_id: m.match_id,
            title: m.title,
            date: m.start_date_obj,
            tslug: m.tournament_slug
        })),
        status: targetStatus
    };

    try {
        await cricketService.toggleLiveBlogBatch(payload);
        setMatches(prev => prev.map(m => {
            if(selectedUpcomingIds.has(m.match_id)) return { ...m, is_enabled: targetStatus };
            return m;
        }));
        toast.success(targetStatus ? "Matches subscribed successfully" : "Matches unsubscribed successfully");
        setUpcomingPopoverOpen(false);
    } catch(err) {
        toast.error("Bulk action failed");
    } finally {
        setBulkLoading(false);
    }
  };


  // --- CALENDAR UTILS ---
  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (view === "month") newDate.setMonth(currentDate.getMonth() + direction);
    else newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const goToToday = () => { setCurrentDate(new Date()); };

  // Handle date selection from the new Picker
  const handleDateSelection = (newDate) => {
    setCurrentDate(newDate);
    setDatePickerOpen(false);
  };

  const handleShowMoreClick = (e, targetId, items, date) => {
    e.stopPropagation();
    if (isMatchModalOpen) return;
    const dayLabel = date.toLocaleDateString('en-US', { weekday: 'short' });
    const dayNum = date.getDate();
    setMorePopoverState({ isOpen: true, targetId: targetId, items: items, dateLabel: { day: dayLabel, num: dayNum }, fullDate: date });
  };

  const closeMorePopover = () => { setMorePopoverState(prev => ({ ...prev, isOpen: false })); };

  const getCalendarDays = () => {
    const days = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (view === "month") {
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
        for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    } else {
        const dayOfWeek = currentDate.getDay(); 
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - dayOfWeek);
        for (let i = 0; i < 7; i++) {
            const d = new Date(startOfWeek);
            d.setDate(startOfWeek.getDate() + i);
            days.push(d);
        }
    }
    return days;
  };

  const getMatchesForDay = (date) => {
    if (!date) return [];
    let displayMatches = matches;
    if (debouncedQuery) {
        const lowerQ = debouncedQuery.toLowerCase();
        displayMatches = displayMatches.filter(m => (m.title && m.title.toLowerCase().includes(lowerQ)) || (m.tournament_name && m.tournament_name.toLowerCase().includes(lowerQ)));
    }
    return displayMatches.filter((item) => {
      const matchDate = new Date(item.start_date_obj);
      const isSameDay = matchDate.toDateString() === date.toDateString();
      if(!isSameDay) return false;
      if(showUpcomingOnly && item.status === 'completed') return false;
      return true;
    });
  };

  const getHeaderText = () => {
      const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      if (view === 'month') return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
      else {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        const startMonth = monthNames[startOfWeek.getMonth()].substring(0,3);
        const endMonth = monthNames[endOfWeek.getMonth()].substring(0,3);
        if (startMonth === endMonth) return `${startMonth} ${startOfWeek.getFullYear()}`;
        return `${startMonth} - ${endMonth} ${endOfWeek.getFullYear()}`;
      }
  };

  // --- RENDERS ---
  const renderModalContent = () => {
    if (!selectedMatch) return null;
    const isCompleted = isMatchPast(selectedMatch);
    
    return (
      <>
           <div className="d-flex justify-content-center mb-3">
               <Badge color={selectedMatch.is_enabled ? "success" : "secondary"} className="p-2 fs-6">
                   {selectedMatch.is_enabled ? "LIVE BLOG ACTIVE" : "DISABLED"}
               </Badge>
           </div>
           <p className="text-center text-muted">
               <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-danger"/>
               {selectedMatch.venue || 'Venue TBD'}
           </p>
           
           {isCompleted ? (
                <div className="text-center py-2">
                    <div className="score-board mb-3" style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fw-bold text-dark">{selectedMatch.teams?.a?.name || 'Team A'}</span>
                            <span className="fw-bold fs-5 text-dark">{selectedMatch.teams?.a?.score || '-'}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold text-dark">{selectedMatch.teams?.b?.name || 'Team B'}</span>
                            <span className="fw-bold fs-5 text-dark">{selectedMatch.teams?.b?.score || '-'}</span>
                        </div>
                        <div className="mt-3 pt-2 border-top text-center">
                            <span className="fw-bold text-success" style={{fontSize: '0.9rem'}}>
                                <FontAwesomeIcon icon={faTrophy} className="me-2" />
                                {selectedMatch.result_msg || 'Match Completed'}
                            </span>
                        </div>
                    </div>
                </div>
           ) : (
                <div className="d-grid gap-2">
                    <Button color={selectedMatch.is_enabled ? "danger" : "success"} onClick={(e) => handleToggle(selectedMatch, e)}>
                        {selectedMatch.is_enabled ? "Unsubscribe" : "Subscribe"} 
                    </Button>
                </div>
           )}
      </>
    );
  };

  const subscribedList = getSubscribedMatchList();
  const bulkSelectedMatches = allUpcomingMatches.filter(m => selectedUpcomingIds.has(m.match_id));
  const isBulkAllActive = bulkSelectedMatches.length > 0 && bulkSelectedMatches.every(m => m.is_enabled);

  return (
    <div className="content gcal-wrapper">
      <ToastContainer position="top-right" autoClose={1000} theme="colored" style={{zIndex: 99999}} />
      
      {/* --- RICH HEADER --- */}
      <div className="header-layout-wrapper">
        <div className="header-main-column">
            {/* Row 1: Logo, Nav, Tools */}
            <div className="gcal-header-row">
                <div className="header-left">
                    <div className="gcal-logo">🏏<span className="d-none d-md-inline">Live Blog Manager</span></div>
                    <div className="header-nav">
                        <button className="today-btn-gcal" onClick={goToToday}>Today</button>
                        <div className="nav-arrows">
                            <button onClick={() => navigateDate(-1)}><FontAwesomeIcon icon={faChevronLeft} /></button>
                            <button onClick={() => navigateDate(1)}><FontAwesomeIcon icon={faChevronRight} /></button>
                        </div>
                        
                        {/* SMART DATE SELECTOR TRIGGER */}
                        <div 
                            className="current-month-display"
                            id="datePickerTarget"
                            onClick={() => setDatePickerOpen(!datePickerOpen)}
                        >
                            <span className="month-text">{getHeaderText()}</span>
                            <FontAwesomeIcon icon={faAngleDown} size="xs" className="dropdown-icon" />
                        </div>
                        <Popover 
                            placement="bottom-start" 
                            isOpen={datePickerOpen} 
                            target="datePickerTarget" 
                            toggle={() => setDatePickerOpen(!datePickerOpen)} 
                            trigger="legacy"
                            className="shadow border-0"
                            style={{zIndex: 2060}}
                        >
                            <PopoverBody className="p-0">
                                <DateSelector 
                                    currentDate={currentDate} 
                                    onDateChange={handleDateSelection}
                                />
                            </PopoverBody>
                        </Popover>
                    </div>
                </div>
                <div className="header-tools-left">
                     <div className="view-switcher me-2 d-none d-md-block">
                         <div className="btn-group">
                            <button className={`view-btn ${view === 'month' ? 'active' : ''}`} onClick={() => setView('month')}><FontAwesomeIcon icon={faCalendarAlt} className="me-1"/> Month</button>
                            <button className={`view-btn ${view === 'week' ? 'active' : ''}`} onClick={() => setView('week')}><FontAwesomeIcon icon={faCalendarWeek} className="me-1"/> Week</button>
                         </div>
                     </div>
                    <div className={`filter-expandable ${showSearch ? 'open' : ''}`}>
                        <input 
                            type="text" 
                            placeholder="Search matches..." 
                            value={filters.query} 
                            onChange={(e) => setFilters(prev => ({ ...prev, query: e.target.value }))}
                        />
                        {/* Cancel Search Button */}
                        {filters.query && (
                            <button 
                                className="btn-clear-search" 
                                onClick={() => setFilters(prev => ({ ...prev, query: '' }))}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </div>
                    <button className={`icon-button ${showSearch ? 'active' : ''}`} onClick={() => setShowSearch(!showSearch)}><FontAwesomeIcon icon={faSearch} /></button>
                </div>
            </div>

            {/* Row 2: Filters */}
            <div className={`filters-bar-row ${isMobileView ? 'mobile-open' : ''}`}>
                <div className="filter-item">
                    <Select
                        placeholder="Filter by Tournament"
                        options={tournaments.map(t => ({ label: t.name, value: t.slug }))}
                        value={tournaments.map(t => ({ label: t.name, value: t.slug })).find(o => o.value === filters.tslug) || null}
                        onChange={(selected) => setFilters(prev => ({ ...prev, tslug: selected ? selected.value : '' }))}
                        isClearable={true}
                        styles={{
                            control: (base) => ({ ...base, minHeight: '30px', height: '30px', fontSize: '12px' }),
                            valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                            input: (base) => ({ ...base, margin: 0, padding: 0 }),
                            indicatorsContainer: (base) => ({ ...base, height: '28px' })
                        }}
                    />
                </div>
                 
                 <div className="d-flex align-items-center ms-md-3 mt-2 mt-md-0" style={{ fontSize: '11px' }}>
                    <input type="checkbox" className="form-check-input me-1" checked={showUpcomingOnly} onChange={(e) => setShowUpcomingOnly(e.target.checked)} />
                    <label className="fw-bold text-secondary mb-0">Upcoming Only</label>
                 </div>
                 <button className="reset-btn-gcal ms-auto mt-2 mt-md-0" onClick={() => { setFilters({query:"", tslug:""}); setShowUpcomingOnly(false); setCurrentDate(new Date()); }}>Reset</button>
            </div>
        </div>

        {/* --- RIGHT COLUMN: STATS --- */}
        <div className="header-stats-column">
             <div className="rich-stats-grid">
                {/* SUBSCRIPTION CARD */}
                <div className="rich-stat-card sub-card" id="subscribedBadgeTarget" onMouseEnter={handleSubscribedEnter} onMouseLeave={handleSubscribedLeave} onClick={() => setSubPopoverOpen(true)}>
                    <div className="rich-icon-box icon-green"><FontAwesomeIcon icon={faBell} /></div>
                    <div className="rich-text-col">
                        <div className="rich-primary-row">
                            <span className="rich-num">{getActiveSubscribedCount()}</span>
                            <span className="rich-label">Active Subs</span>
                        </div>
                        <div className="rich-sub-row text-success-muted">
                             <FontAwesomeIcon icon={faCheckCircle} size="xs" className="me-1"/>{getCompletedSubscribedCount()} Finished
                        </div>
                    </div>
                </div>
                <div className="rich-divider d-none d-xl-block"></div>
                {/* DATABASE CARD */}
                <div className="rich-stat-card db-card" id="upcomingStatsTarget" onMouseEnter={handleUpcomingEnter} onMouseLeave={handleUpcomingLeave} onClick={() => setUpcomingPopoverTab('matches')}>
                     <div className="rich-icon-box icon-blue"><FontAwesomeIcon icon={faCalendarAlt} /></div>
                     <div className="rich-text-col">
                        <div className="rich-primary-row">
                            <span className="rich-num">{allUpcomingMatches.length}</span>
                            <span className="rich-label">Upcoming</span>
                        </div>
                        <div className="rich-sub-row text-primary-muted">
                            <FontAwesomeIcon icon={faTrophy} size="xs" className="me-1"/>{upcomingTournamentsList.length} Tournaments
                        </div>
                     </div>
                </div>
             </div>
        </div>
      </div>

      {/* --- CALENDAR GRID --- */}
      <div className="calendar-container" ref={calendarRef} onWheel={handleWheelScroll}>
        {loading && <div className="loading-overlay"><Spinner color="primary" style={{ width: '3rem', height: '3rem' }} /></div>}
        <div className="days-header">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                <div key={day} className="day-name">
                    <span className="d-none d-md-block">{day}</span>
                    <span className="d-block d-md-none">{day.charAt(0)}</span>
                </div>
            ))}
        </div>
        <div className={`days-grid ${view === 'week' ? 'week-view' : ''}`}>
          {getCalendarDays().map((date, index) => {
            if (!date) return <div key={index} className="day-cell empty"></div>;
            const isCurrentDay = (date.toDateString() === new Date().toDateString());
            const dayMatches = getMatchesForDay(date);
            const groupedMatches = dayMatches.reduce((groups, matchItem) => {
            const tName = matchItem.tournament_name || "Unknown";
            const tSlug = matchItem.tournament_slug; 
            
            if (!groups[tName]) {
                groups[tName] = { 
                    info: { name: tName, slug: tSlug }, // Store slug in info
                    matches: [] 
                };
            }
            groups[tName].matches.push(matchItem);
            return groups;
          }, {});
            const groupsArray = Object.values(groupedMatches);
            const MAX_VISIBLE_ITEMS = view === 'week' ? 10 : (isMobileView ? 4 : 2); 
            const visibleGroups = groupsArray.slice(0, MAX_VISIBLE_ITEMS);
            const hiddenCount = groupsArray.length - MAX_VISIBLE_ITEMS;
            const uniqueMoreId = `more-link-${date.getTime()}`;

            return (
              <div key={index} className={`day-cell ${isCurrentDay ? "today" : ""}`}
                   onClick={(e) => isMobileView && groupsArray.length > 0 && handleShowMoreClick(e, uniqueMoreId, groupsArray, date)}
              >
                <div className="day-number-row">
                    <span className={`day-number ${isCurrentDay ? 'active' : ''}`}>{date.getDate()}</span>
                    {view === 'week' && <span className="ms-2 text-muted small">{date.toLocaleDateString('en-US', {weekday:'short'})}</span>}
                </div>
                <div className="events-container">
                    {visibleGroups.map((group, gIndex) => (
                         <CricketTournamentGroup 
                            key={`${date.getDate()}-${gIndex}`} 
                            group={group} 
                            date={date} 
                            toggling={toggling} 
                            handleToggle={handleToggle} 
                            setSelectedMatch={handleOpenMatchModal}
                            isMatchModalOpen={isMatchModalOpen}
                            handleGroupBatchToggle={handleGroupBatchToggle}
                            allMatches={matches}
                            isMobileView={isMobileView}
                         />
                    ))}
                    {hiddenCount > 0 && (
                        <div id={uniqueMoreId} className="more-events-link" onClick={(e) => handleShowMoreClick(e, uniqueMoreId, groupsArray, date)}>
                            {hiddenCount} more +
                        </div>
                    )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* --- SUBSCRIPTION POPOVER --- */}
      {!isMatchModalOpen && (
          <Popover placement="bottom-end" isOpen={subPopoverOpen} target="subscribedBadgeTarget" toggle={() => {}} trigger="legacy" container="body" className="custom-popover-shadow super-priority-popover" style={{zIndex: 1060}}>
              <PopoverBody className="p-0" onMouseEnter={handleSubscribedEnter} onMouseLeave={handleSubscribedLeave}>
                <div className="popover-header-modern bg-success text-white border-0"><strong>Active Subscriptions</strong></div>
                <div className="popover-matches-list" style={{ maxHeight: '300px' }}>
                    {subscribedList.length === 0 ? <div className="p-3 text-center text-muted small">No active matches found.</div> : (
                        subscribedList.map(m => {
                            const isCompleted = isMatchPast(m);
                            const isLive = isMatchLive(m);
                            let rowClass = "popover-match-row";
                            if(isCompleted) rowClass += " row-is-completed";
                            else if (isLive) rowClass += " row-is-live";
                            return (
                                <div key={m.match_id} className={rowClass} onClick={() => handleOpenMatchModal(m)} style={{cursor: 'pointer'}}>
                                    <div className="match-time-col" style={{width: '90px'}}>{isCompleted ? "Ended" : isLive ? "LIVE" : new Date(m.start_date_obj).toLocaleDateString(undefined, {month:'short', day:'numeric'})}</div>
                                    <div className="match-details-col">
                                        <div className="match-teams">{m.title}</div>
                                    </div>
                                    <div className="ms-2"><FontAwesomeIcon icon={faCheckCircle} className="text-success" /></div>
                                </div>
                            )
                        })
                    )}
                </div>
              </PopoverBody>
          </Popover>
      )}

      {/* --- UPCOMING / DB POPOVER --- */}
      {!isMatchModalOpen && (
        <Popover placement="bottom-end" isOpen={upcomingPopoverOpen} target="upcomingStatsTarget" toggle={() => {}} trigger="legacy" container="body" className="custom-popover-shadow super-priority-popover" style={{ maxWidth: '350px', zIndex: 1060 }}>
             <PopoverBody className="p-0" onMouseEnter={handleUpcomingEnter} onMouseLeave={handleUpcomingLeave}>
                 {/* Tabs */}
                <div className="popover-header-modern bg-light border-bottom p-0">
                    <Nav tabs className="border-0">
                        <NavItem className="flex-fill text-center" style={{cursor: 'pointer'}}>
                            <NavLink className={classnames({ active: upcomingPopoverTab === 'matches' })} onClick={() => setUpcomingPopoverTab('matches')} style={{borderRadius: 0, padding: '10px 0', fontSize: '12px', fontWeight: 'bold'}}>
                                Matches ({allUpcomingMatches.length})
                            </NavLink>
                        </NavItem>
                        <NavItem className="flex-fill text-center" style={{cursor: 'pointer'}}>
                             <NavLink className={classnames({ active: upcomingPopoverTab === 'tournaments' })} onClick={() => setUpcomingPopoverTab('tournaments')} style={{borderRadius: 0, padding: '10px 0', fontSize: '12px', fontWeight: 'bold'}}>
                                Tournaments ({upcomingTournamentsList.length})
                             </NavLink>
                        </NavItem>
                    </Nav>
                </div>

                {/* Tab: Matches */}
                {upcomingPopoverTab === 'matches' && (
                    <>
                    <div className="p-2 bg-white border-bottom">
                         <div className="d-flex align-items-center bg-white p-2 border rounded shadow-sm">
                             <input className="form-check-input m-0" type="checkbox" checked={selectedUpcomingIds.size === allUpcomingMatches.length && allUpcomingMatches.length > 0} onChange={toggleSelectAll} style={{ cursor: 'pointer', width: '18px', height: '18px' }}/>
                             <span className="ms-2 small fw-bold text-secondary">Select All</span>
                         </div>
                    </div>
                    <div className="popover-matches-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                         {allUpcomingMatches.length === 0 ? <div className="p-3 text-center text-muted"><small>No upcoming matches.</small></div> : (
                             allUpcomingMatches.map(m => {
                                 const isSelected = selectedUpcomingIds.has(m.match_id);
                                 const isSubbed = m.is_enabled;
                                 let rowClass = "popover-match-row";
                                 if(isSubbed) rowClass += " row-is-active";
                                 return (
                                     <div key={m.match_id} className={rowClass} onClick={() => toggleMatchSelection(m.match_id)} style={{cursor: 'pointer'}}>
                                         <div className="me-2"><input className="form-check-input" type="checkbox" checked={isSelected} readOnly style={{pointerEvents: 'none'}} /></div>
                                         <div className="match-time-col" style={{width:'90px'}}>{new Date(m.start_date_obj).toLocaleDateString(undefined, {month:'short', day:'numeric'})}</div>
                                         <div className="match-details-col"><div className="match-teams">{m.title}</div></div>
                                         {isSubbed && <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2 small" />}
                                     </div>
                                 )
                             })
                         )}
                    </div>
                    <div className="p-2 border-top bg-white">
                         <Button color={isBulkAllActive ? "danger" : "success"} size="sm" className="w-100 btn-bulk-subscribe" onClick={handleBulkAction} disabled={bulkLoading || selectedUpcomingIds.size === 0}>
                            {bulkLoading ? <Spinner size="sm"/> : <FontAwesomeIcon icon={isBulkAllActive ? faTrash : faLayerGroup} className="me-1" />}
                            {isBulkAllActive ? `Unsubscribe Selected (${selectedUpcomingIds.size})` : `Subscribe Selected (${selectedUpcomingIds.size})`}
                         </Button>
                    </div>
                    </>
                )}

                {/* Tab: Tournaments */}
                {upcomingPopoverTab === 'tournaments' && (
                    <div className="popover-matches-list" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                        {upcomingTournamentsList.map(group => {
                            const activeCount = group.matches.filter(m => m.is_enabled).length;
                            const isAllActive = activeCount === group.matches.length;
                            return (
                                <div key={group.tSlug} className="p-3 border-bottom hover-bg-light">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div className="fw-bold text-dark" style={{fontSize: '13px'}}>{group.info.name}</div>
                                        <Badge color="secondary" pill>{group.matches.length}</Badge>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                         <div className="text-muted small">{activeCount} Subscribed</div>
                                         <Button size="sm" color={isAllActive ? "outline-danger" : "outline-info"} style={{fontSize: '10px', padding: '2px 8px'}} onClick={(e) => { e.stopPropagation(); handleGroupBatchToggle(group, !isAllActive); }}>
                                             {isAllActive ? "Unsubscribe All" : "Subscribe All"}
                                         </Button>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
             </PopoverBody>
        </Popover>
      )}

      {/* --- MORE EVENTS POPOVER --- */}
      {morePopoverState.isOpen && (
        <Popover placement="auto" isOpen={morePopoverState.isOpen} target={morePopoverState.targetId} toggle={closeMorePopover} className="more-events-popover" trigger="legacy" container="body" style={{ zIndex: 1070 }}>
          <PopoverBody className="p-0">
             <div className="more-popover-header">
                <div className="d-flex flex-column align-items-center">
                    <span className="more-popover-day">{morePopoverState.dateLabel?.day}</span>
                    <span className="more-popover-num">{morePopoverState.dateLabel?.num}</span>
                </div>
                <button className="btn-close-custom" onClick={closeMorePopover}><FontAwesomeIcon icon={faTimes} /></button>
             </div>
             <div className="more-popover-list">
                {morePopoverState.items.map((group, idx) => (
                     <CricketTournamentGroup 
                        key={`more-${idx}`} 
                        group={group} 
                        date={morePopoverState.fullDate} 
                        toggling={toggling} 
                        handleToggle={handleToggle} 
                        setSelectedMatch={handleOpenMatchModal} 
                        isMatchModalOpen={isMatchModalOpen} 
                        handleGroupBatchToggle={handleGroupBatchToggle} 
                        allMatches={matches}
                        isMobileView={false} // Always show full chips inside "More" popover
                     />
                ))}
             </div>
          </PopoverBody>
        </Popover>
      )}

      {/* --- MATCH MODAL --- */}
      {selectedMatch && (
        <Modal isOpen={!!selectedMatch} toggle={handleCloseMatchModal} centered modalClassName="match-modal-container">
          <div className="modal-header" style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', borderBottom: 'none' }}>
            <div className="text-center w-100">
              <h5 className="modal-title fw-bold mb-1">{selectedMatch.title}</h5>
              <small className="opacity-75">
                <FontAwesomeIcon icon={faClock} className="me-1" />
                {new Date(selectedMatch.start_date_obj).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
              </small>
            </div>
          </div>
          <ModalBody className="p-4">{renderModalContent()}</ModalBody>
        </Modal>
      )}

      {/* --- STYLES --- */}
      <style>{`
        /* --- Z-INDEX PRIORITIES --- */
        .Toastify__toast-container { z-index: 100000 !important; }
        .header-priority-popover { z-index: 10000 !important; }
        .header-priority-popover .popover { z-index: 10000 !important; }
        .match-modal-container { z-index: 5000 !important; }
        .modal-backdrop { z-index: 4999 !important; }
        .custom-popover-shadow { z-index: 3000 !important; box-shadow: 0 8px 24px rgba(0,0,0,0.15); border: none; border-radius: 8px; }
        .super-priority-popover { z-index: 10000 !important; }

        /* --- HEADER LAYOUT & COMPACT MODE --- */
        .header-layout-wrapper { display: flex; flex-wrap: wrap; width: 100%; border-bottom: 1px solid #dadce0; background: #fff; position: relative; }
        .header-main-column { flex: 1 1 500px; display: flex; flex-direction: column; min-width: 0; }
        
        .gcal-header-row { 
            display: flex; 
            align-items: center; 
            justify-content: space-between; 
            padding: 2px 12px; /* COMPACT PADDING */
            min-height: 38px; /* REDUCED HEIGHT */
            flex-shrink: 0; 
            flex-wrap: wrap; 
            gap: 8px; 
            border-bottom: 1px solid #f1f3f4; 
        }

        .header-left { display: flex; align-items: center; gap: 10px; }
        .gcal-logo { display: flex; align-items: center; gap: 6px; font-size: 16px; color: #5f6368; white-space: nowrap; }
        .header-nav { display: flex; align-items: center; gap: 8px; }
        .today-btn-gcal { padding: 2px 10px; border: 1px solid #dadce0; border-radius: 4px; background: #fff; color: #3c4043; font-weight: 500; font-size: 11px; cursor: pointer; white-space: nowrap;}
        .today-btn-gcal:hover { background: #f1f3f4; }
        .nav-arrows { display: flex; align-items: center; }
        .nav-arrows button { background: none; border: none; cursor: pointer; color: #5f6368; padding: 4px; border-radius: 50%; font-size: 12px; margin: 0 1px; }
        .nav-arrows button:hover { background: #f1f3f4; color: #202124; }
        .current-month-display { font-size: 14px; color: #3c4043; min-width: 90px; white-space: nowrap; cursor: pointer; display: flex; align-items: center; gap: 4px;}
        .dropdown-icon { opacity: 0.5; }
        .header-tools-left { display: flex; align-items: center; gap: 4px; margin-left: auto; }
        
        /* DATE PICKER */
        .custom-date-picker { padding: 10px; width: 240px; }
        .picker-header { display: flex; justify-content: center; align-items: center; margin-bottom: 10px; font-size: 15px; font-weight: bold; }
        .picker-btn { cursor: pointer; padding: 2px 6px; border-radius: 4px; }
        .picker-btn:hover { background: #f1f3f4; }
        .picker-btn.active { color: #1a73e8; }
        .picker-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
        .picker-cell { text-align: center; padding: 6px 4px; cursor: pointer; border-radius: 4px; font-size: 12px; }
        .picker-cell:hover { background: #f1f3f4; }
        .picker-cell.selected { background: #1a73e8; color: white; }

        /* Filters Row - Compact */
        .filters-bar-row { 
            display: flex; 
            align-items: center; 
            padding: 2px 12px; 
            background: #f8f9fa; 
            border-bottom: 1px solid #e0e0e0; 
            min-height: 38px; /* REDUCED HEIGHT */
            flex-wrap: wrap;
            gap: 8px;
        }
        .filter-item { flex: 1; min-width: 130px; font-size: 11px; max-width: 180px; } 

        /* RICH HEADER STATS - Compact */
       .header-stats-column {
            flex: 0 0 auto;
            display: flex;
            align-items: center;
            padding: 0 16px;       /* Original padding */
            border-left: 1px solid #dadce0;
            background: #fff;
            min-height: 44px;      /* Original Height */
        }
        
        .rich-stats-grid { 
            display: flex; 
            align-items: center; 
            gap: 12px;             /* Original gap */
            height: 100%; 
            width: 100%; 
        }

        .rich-stat-card {
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 8px 16px;
            cursor: pointer;
            border-radius: 12px;
            transition: background 0.2s;
            height: 38px;
            white-space: nowrap; /* Prevent text wrapping inside cards */
        }

        .rich-stat-card:hover {
            background-color: #f8f9fa;
        }

        .rich-icon-box {
            width: 42px;
            height: 42px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            flex-shrink: 0; /* Prevent icon from squashing */
        }        .icon-green { background-color: #e6f4ea; color: #137333; }
        .icon-blue { background-color: #e8f0fe; color: #1a73e8; }
        .rich-text-col { display: flex; flex-direction: column; justify-content: center; line-height: 1.1; }
        .rich-primary-row { display: flex; align-items: baseline; gap: 3px; }
        .rich-num { font-size: 13px; font-weight: 800; color: #202124; }
        .rich-label { font-size: 9px; font-weight: 700; text-transform: uppercase; color: #5f6368; }
        .rich-sub-row { font-size: 9px; font-weight: 600; margin-top: 1px; white-space: nowrap; }
        .text-success-muted { color: #1e8e3e; opacity: 0.9; }
        .text-primary-muted { color: #1967d2; opacity: 0.9; }
        .rich-divider { width: 1px; height: 0px; background-color: #dadce0; margin: 0 2px; }

        /* MOBILE MENU & RESPONSIVE LOGIC */
        .mobile-menu-btn { display: none; }
        
        @media (max-width: 1200px) {
             .header-main-column { border-right: none; border-bottom: 1px solid #f1f3f4; }
             .header-stats-column { border-left: none; justify-content: space-around; background-color: #fcfcfc; padding: 6px 12px; }
        }
        @media (max-width: 768px) {
             .mobile-menu-btn { display: flex; }
             .filters-bar-row { display: none; flex-direction: column; align-items: stretch; padding-bottom: 10px; height: auto; min-height: auto;}
             .filters-bar-row.mobile-open { display: flex; }
             .filter-item { max-width: 100%; }
             /* Hide text on mobile stats to save space */
             .rich-label { display: none; }
             /* IMPORTANT: Enable scrolling on mobile by removing fixed height */
             .gcal-wrapper { height: auto !important; min-height: 600px; overflow: visible !important; }
             .calendar-container { overflow: visible !important; }
             .days-grid { min-height: 500px; grid-template-rows: repeat(auto-fit, minmax(80px, 1fr)) !important; }
        }

        /* General Calendar Styles */
        .gcal-wrapper { position: relative; top: -10px !important; height: calc(100vh - 95px); display: flex; flex-direction: column; background: #ffffff; font-family: 'Google Sans', 'Roboto', Arial, sans-serif; overflow: hidden; border-radius: 8px; border: 1px solid #dadce0; padding-top: 0 !important; }
        
        .icon-button { background: none; border: none; cursor: pointer; width: 28px; height: 28px; border-radius: 50%; color: #5f6368; font-size: 14px; display: flex; align-items: center; justify-content: center; }
        .icon-button:hover { background: #f1f3f4; }
        .icon-button.active { background: #e8f0fe; color: #1a73e8; }
        .view-switcher .btn-group { border: 1px solid #dadce0; border-radius: 4px; overflow: hidden; display: flex; }
        .view-btn { border: none; background: #fff; color: #5f6368; padding: 3px 10px; font-size: 11px; font-weight: 500; cursor: pointer; transition: all 0.2s; border-right: 1px solid #dadce0; white-space: nowrap; }
        .view-btn:last-child { border-right: none; }
        .view-btn:hover { background: #f1f3f4; }
        .view-btn.active { background: #dc3545; color: white; }
        .filter-expandable { width: 0; overflow: hidden; transition: width 0.3s ease; position: relative;}
        .filter-expandable.open { width: 140px; }
        .filter-expandable input { width: 100%; padding: 2px 24px 2px 6px; border: 1px solid #dadce0; border-radius: 4px; background: #f1f3f4; border: none; font-size: 11px; }
        .reset-btn-gcal { background: none; border: none; color: #1a73e8; font-weight: 500; cursor: pointer; padding: 0; font-size: 11px; white-space: nowrap; }
        .btn-bulk-subscribe { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .btn-clear-search {position: absolute;right: 2px;top: 50%;transform: translateY(-50%);background: none;border: none;color: #5f6368;cursor: pointer;font-size: 10px;padding: 0;width: 20px;height: 100%;display: flex;align-items: center;justify-content: center;}
        .btn-clear-search:hover {color: #202124;}
        .calendar-container { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; }
        .loading-overlay { position: absolute; top:0; left:0; width:100%; height:100%; background: rgba(255,255,255,0.8); z-index: 50; display: flex; justify-content: center; align-items: center; }
        
        /* GRID FIXES */
        .days-header { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); border-bottom: 1px solid #dadce0; padding-top: 4px; padding-bottom: 2px; }
        .day-name { text-align: center; color: #70757a; font-size: 11px; font-weight: 800; text-transform: uppercase; padding-bottom: 4px; border-left: 1px solid transparent; }
        .day-name:first-child { border-left: 1px solid #dadce0; }
        .days-grid { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); grid-template-rows: repeat(auto-fit, minmax(0, 1fr)); flex: 1; height: 100%; border-left: 1px solid #dadce0; }
        .days-grid.week-view { grid-template-rows: 1fr; }
        .day-cell { border-right: 1px solid #dadce0; border-bottom: 1px solid #dadce0; min-height: 0; display: flex; flex-direction: column; padding: 0px 2px; background: #fff; }
        .day-cell.empty { background: #f8f9fa; }
        .day-cell.today { background: #fff; } 
        .day-number-row { text-align: center; margin-bottom: 0px; padding-top: 2px; transition: margin 0.2s; }
        .day-number { display: inline-block; width: 20px; height: 20px; line-height: 20px; border-radius: 50%; font-size: 11px; font-weight: 700; color: #3c4043; transition: all 0.2s; }
        .day-cell.today .day-number.active { background: #1a73e8; color: #fff;  }
        .events-container { flex: 1; overflow: hidden; display: flex; flex-direction: column; gap: 1px; }
        .event-group-wrapper { width: 100%; }
        
        /* CHIPS */
        .event-chip { border-radius: 3px; padding: 0px 4px; font-size: 9px; cursor: pointer; display: flex; align-items: center; gap: 4px; width: 100%; transition: all 0.1s; border: 1px solid transparent; height: 16px; }
        .event-chip:hover { box-shadow: 0 1px 3px rgba(0,0,0,0.15); }
        .chip-upcoming { background: #fff; border-color: #4285f4; color: #1967d2; }
        .chip-live { background: #fce8e6; border-color: #ea4335; color: #c5221f; font-weight: 700; }
        .chip-active { background: #e6f4ea; border-color: #34a853; color: #137333; font-weight: 600; }
        .chip-active-completed { background: #e8eaed; border-color: #0d652d; color: #0d652d; opacity: 0.8; }
        .chip-completed { background: #f1f3f4; border-color: #dadce0; color: #5f6368; }
        .live-dot { height: 5px; width: 5px; background-color: #c5221f; border-radius: 50%; display: inline-block; animation: pulse 1.5s infinite; }
        @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.4; } 100% { opacity: 1; } }
        .count-badge { font-weight: 700; }
        .title-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; }
        
        /* MOBILE DOT VIEW */
        .mobile-dot-view {
            padding: 0;
            justify-content: center;
            border: none !important;
            background: transparent !important;
            height: 8px;
        }

        .more-events-link { font-size: 10px; font-weight: 600; color: #3c4043; cursor: pointer; padding: 0px 4px; border-radius: 4px; margin-top: 1px; transition: background 0.2s; }
        .more-events-link:hover { background-color: #f1f3f4; color: #202124; }
        .more-events-popover { border: none; box-shadow: 0 8px 24px rgba(0,0,0,0.15); min-width: 250px; max-width: 300px; border-radius: 8px; }
        .more-popover-header { display: flex; justify-content: space-between; align-items: flex-start; padding: 12px 16px 8px; }
        .more-popover-day { font-size: 11px; color: #70757a; font-weight: 600; text-transform: uppercase; line-height: 1; }
        .more-popover-num { font-size: 22px; color: #3c4043; line-height: 1; }
        .btn-close-custom { background: none; border: none; color: #5f6368; cursor: pointer; font-size: 16px; padding: 0; }
        .btn-close-custom:hover { color: #202124; }
        .more-popover-list { padding: 8px 12px 16px; display: flex; flex-direction: column; gap: 4px; max-height: 350px; overflow-y: auto; }
        .popover-header-modern { padding: 16px; background: #f8f9fa; border-bottom: 1px solid #eee; border-radius: 8px 8px 0 0; }
        .chip-live-header { background: #fce8e6; border-bottom-color: #fad2cf; }
        .chip-active-header { background: #e6f4ea; border-bottom-color: #ceead6; }
        .chip-active-completed-header { background: #e8eaed; border-bottom-color: #dadce0; }
        .popover-tournament-name { font-size: 14px; font-weight: 700; color: #202124; margin-bottom: 4px; }
        .popover-date-badge { font-size: 12px; color: #5f6368; display: flex; align-items: center; }
        .popover-matches-list { max-height: 300px; overflow-y: auto; padding: 4px 0; }
        .popover-match-row { display: flex; align-items: center; padding: 10px 16px; border-bottom: 1px solid #f1f3f4; transition: background 0.2s; border-left: 3px solid transparent; }
        .popover-match-row:last-child { border-bottom: none; }
        .popover-match-row:hover { background: #f8f9fa; }
        .row-is-live { border-left-color: #ea4335; background: #fff8f7; }
        .row-is-active { border-left-color: #34a853; background: #f6fbf7; }
        .row-was-active { border-left-color: #0d652d; background: #f1f8f3; opacity: 0.9; }
        .row-is-completed { border-left-color: #dadce0; opacity: 0.7; }
        .status-live { color: #c5221f; font-weight: 800; font-size: 10px; animation: pulse 1.5s infinite; }
        .match-time-col { width: 60px; font-size: 11px; color: #5f6368; font-weight: 500; }
        .status-finished { color: #5f6368; font-weight: 700; background: #e8eaed; padding: 2px 4px; border-radius: 4px; font-size: 10px; }
        .match-details-col { flex: 1; overflow: hidden; cursor: pointer; }
        .match-teams { font-size: 13px; font-weight: 600; color: #3c4043; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .match-teams .vs { font-weight: 400; color: #9aa0a6; font-size: 11px; margin: 0 4px; }
        .match-subtext { font-size: 11px; color: #70757a; margin-top: 2px; }
        .match-action-col { margin-left: 12px; }
        .action-btn { padding: 0; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border-radius: 50%; }
        .action-btn:hover { background: #e8f0fe; }d
        .icon-active { color: #34a853; }
        .icon-inactive { color: #dadce0; }
        .hover-bg-light:hover { background-color: #f8f9fa; }
      `}</style>
    </div>
  );
};

export default CricketLiveBlogManager;